import Accordion from 'components/Accordion';
import {
  Checkbox as MuiCheckbox,
  FormControlLabel as MuiFormControlLabel,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { Search } from 'components/ui';
import LoadingPage from 'components/LoadingPage';
import { useDebounce } from 'use-debounce';
import { useGetCarePlansForList } from 'pages/CarePlans/_service/hooks/useGetCarePlansForList';
import { mergeAndSortArrays } from 'lib/utils/returnUniqueIdValues';

export const carePlansList = [
  {
    value: 'activeRequestGroupPlanDefinitionIds',
    label: 'Has active care plan',
  },
  {
    value: 'completedRequestGroupPlanDefinitionIds',
    label: 'Has completed care plan',
  },
  { value: 'hasNoRequestGroups', label: 'Has no care plans' },
];

export default function CarePlanFilter({ setFilters, filters, defaultExpanded = false }) {
  const { t } = useTranslation();
  const theme = useTheme()

  const {
    activeRequestGroupPlanDefinitionIds,
    completedRequestGroupPlanDefinitionIds,
  } = filters;

  const [activeCPTitle, setActiveCPTitle] = useState('');
  const [completedCPTitle, setCompletedCPTitle] = useState('');
  const [activeCPFilter] = useDebounce(activeCPTitle, 300);
  const [completedCPFilter] = useDebounce(completedCPTitle, 300);

  const activeCPListRef = useRef(null);
  const completedCPListRef = useRef(null);

  const {
    carePlans: activeCarePlans,
    loading: loadingActiveCP,
    loadMore: loadMoreActive,
  } = useGetCarePlansForList({
    perPage: 10,
    filter: { title: activeCPFilter, status: 'active' },
  });

  const {
    carePlans: completedCarePlans,
    loading: loadingCompletedCP,
    loadMore: loadMoreCompleted,
  } = useGetCarePlansForList({
    perPage: 10,
    filter: { title: completedCPFilter, status: 'active' },
  });

  useEffect(() => {
    let activeTimer;
    let completedTimer;

    const handleScroll = (listRef, loadMoreFunc, setTimer) => {
      if (listRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
          clearTimeout(setTimer);

          setTimer = setTimeout(() => {
            loadMoreFunc();
          }, 200);
        }
      }
    };

    const activeCpDiv = activeCPListRef.current;
    const completedCpDiv = completedCPListRef.current;

    const onActiveScroll = () =>
      handleScroll(activeCPListRef, loadMoreActive, activeTimer);
    const onCompletedScroll = () =>
      handleScroll(completedCPListRef, loadMoreCompleted, completedTimer);

    if (activeCpDiv) {
      activeCpDiv.addEventListener('scroll', onActiveScroll);
    }

    if (completedCpDiv) {
      completedCpDiv.addEventListener('scroll', onCompletedScroll);
    }

    return () => {
      if (activeCpDiv) {
        activeCpDiv.removeEventListener('scroll', onActiveScroll);
      }
      if (completedCpDiv) {
        completedCpDiv.removeEventListener('scroll', onCompletedScroll);
      }
      clearTimeout(activeTimer);
      clearTimeout(completedTimer);
    };
  }, [loadMoreActive, loadMoreCompleted]);

  const handleCategoryClick = (idx, value) => {
    // Adding boolean filter to query
    switch (idx) {
      case 0: {
        setFilters((curr) => ({
          ...curr,
          activeRequestGroupPlanDefinitionIds: !activeRequestGroupPlanDefinitionIds
            ? []
            : null,
        }));
        break;
      }
      case 1: {
        setFilters((curr) => ({
          ...curr,
          completedRequestGroupPlanDefinitionIds: !completedRequestGroupPlanDefinitionIds
            ? []
            : null,
        }));
        break;
      }
      default: {
        setFilters((curr) => ({
          ...curr,
          hasNoRequestGroups: value,
        }));
      }
    }
  };

  const handleCarePlanClick = (idx, carePlan) => {
    // Handling click on care plan inside of category
    switch (idx) {
      case 0: {
        const isPresent = !!activeRequestGroupPlanDefinitionIds.find(
          (item) => item.id === carePlan.id
        );

        if (isPresent) {
          setFilters((curr) => ({
            ...curr,
            activeRequestGroupPlanDefinitionIds: activeRequestGroupPlanDefinitionIds.filter(
              (item) => item.id !== carePlan.id
            ),
          }));
        } else {
          setFilters((curr) => ({
            ...curr,
            activeRequestGroupPlanDefinitionIds: [
              ...activeRequestGroupPlanDefinitionIds,
              carePlan,
            ],
          }));
        }
        break;
      }
      default: {
        const isPresent = !!completedRequestGroupPlanDefinitionIds.find(
          (item) => item.id === carePlan.id
        );

        if (isPresent) {
          setFilters((curr) => ({
            ...curr,
            completedRequestGroupPlanDefinitionIds: completedRequestGroupPlanDefinitionIds.filter(
              (item) => item.id !== carePlan.id
            ),
          }));
        } else {
          setFilters((curr) => ({
            ...curr,
            completedRequestGroupPlanDefinitionIds: [
              ...completedRequestGroupPlanDefinitionIds,
              carePlan,
            ],
          }));
        }
        break;
      }
    }
  };

  return (
    <Accordion title={t('Care plan')} defaultExpanded={defaultExpanded}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {carePlansList.map((category, categoryIdx) => (
          <div
            key={categoryIdx}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <MuiFormControlLabel
              control={
                <MuiCheckbox
                  checked={!!filters?.[category.value]}
                  onClick={(e) => {
                    handleCategoryClick(categoryIdx, e.target.checked);
                  }}
                />
              }
              label={t(category.label)}
            />
            {filters?.[category.value] && categoryIdx !== 2 && (
              <>
                <Search
                  style={{ borderRadius: 6 }}
                  small
                  placeholder={t('Search care plan')}
                  onChange={(e) => {
                    categoryIdx === 0
                      ? setActiveCPTitle(e.target.value)
                      : setCompletedCPTitle(e.target.value);
                  }}
                />
                <div
                  ref={categoryIdx === 0 ? activeCPListRef : completedCPListRef}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 10,
                    borderRadius: 6,
                    border: `1px solid ${theme.palette.colors.gray200}`,
                    maxHeight: '400px',
                    overflowY: 'auto',
                  }}
                >
                  {(!!activeRequestGroupPlanDefinitionIds && loadingActiveCP) ||
                  (!!completedRequestGroupPlanDefinitionIds &&
                    loadingCompletedCP) ? (
                    <LoadingPage
                      style={{ maxWidh: 100, maxHeight: 100 }}
                      size={30}
                    />
                  ) : categoryIdx === 0 ? (
                    mergeAndSortArrays(
                      activeRequestGroupPlanDefinitionIds,
                      activeCarePlans,
                      'id'
                    )?.map?.((carePlan) => {
                      return (
                        <MuiFormControlLabel
                          key={carePlan.id}
                          label={carePlan.title}
                          control={
                            <MuiCheckbox
                              checked={
                                !!activeRequestGroupPlanDefinitionIds.find(
                                  (item) => item.id === carePlan.id
                                )
                              }
                              onClick={() =>
                                handleCarePlanClick(categoryIdx, carePlan)
                              }
                            />
                          }
                        />
                      );
                    })
                  ) : (
                    mergeAndSortArrays(
                      completedRequestGroupPlanDefinitionIds,
                      completedCarePlans,
                      'id'
                    )?.map?.((carePlan) => {
                      return (
                        <MuiFormControlLabel
                          key={carePlan.id}
                          label={carePlan.title}
                          control={
                            <MuiCheckbox
                              checked={
                                !!completedRequestGroupPlanDefinitionIds.find(
                                  (item) => item.id === carePlan.id
                                )
                              }
                              onClick={() =>
                                handleCarePlanClick(categoryIdx, carePlan)
                              }
                            />
                          }
                        />
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </Accordion>
  );
}
